import { useEffect, useRef, useState } from 'react';

/**
 * Latch `active` on for at least `minMs` once it first turns true, so a brief
 * async action (a chat turn the server answers in 80ms) still shows its loading
 * affordance long enough to read — no flicker, every click gets an animation.
 *
 * Turning on is immediate (a pure render-time state adjust — React's prev-props
 * pattern, no clock read); the delayed turn-off lives entirely in the effect,
 * where reading the clock and flipping state from the timer callback is allowed.
 */
export function useMinVisible(active: boolean, minMs = 600): boolean {
  const [visible, setVisible] = useState(active);
  const [prevActive, setPrevActive] = useState(active);
  const shownAt = useRef(0);

  if (active !== prevActive) {
    setPrevActive(active);
    if (active && !visible) setVisible(true); // immediate ON, no clock read
  }

  useEffect(() => {
    if (active) {
      shownAt.current = Date.now();
      return;
    }
    if (!visible) return;
    const remaining = Math.max(0, minMs - (Date.now() - shownAt.current));
    const timer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(timer);
  }, [active, visible, minMs]);

  return visible;
}
